// src/pages/rh/DetailEmploye.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Badge, Spinner, Alert, 
    Modal, Table, Form, Tab, Nav, Image 
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

// Composant Toast
const ToastNotification = ({ show, onClose, type, title, message }) => {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(() => onClose(), 5000);
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    const toastConfig = {
        success: { bgColor: '#10b981', icon: 'ri-checkbox-circle-fill' },
        error: { bgColor: '#ef4444', icon: 'ri-error-warning-fill' },
        info: { bgColor: '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: '#f59e0b', icon: 'ri-alert-fill' }
    };

    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed" style={{ 
            position: 'fixed', 
            bottom: '20px', 
            right: '20px', 
            zIndex: 9999 
        }}>
            <div className="toast-modern" style={{ 
                background: 'white', 
                borderRadius: '16px', 
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                minWidth: '320px',
                maxWidth: '400px',
                overflow: 'hidden',
                animation: 'slideInRight 0.3s ease-out'
            }}>
                <div className="toast-header-custom" style={{ 
                    background: config.bgColor, 
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom" style={{ padding: '16px 20px' }}>
                    <p className="mb-0" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{message}</p>
                </div>
                <div className="toast-progress" style={{ 
                    height: '3px', 
                    background: config.bgColor,
                    width: '100%',
                    animation: 'shrink 5s linear forwards'
                }}></div>
            </div>
        </div>
    );
};

// Composant de modal pour ajouter un document
const AddDocumentModal = ({ show, onHide, onAdd, employeId }) => {
    const [document, setDocument] = useState({
        type_document: 'CNI',
        nom_document: '',
        date_expiration: '',
        fichier: null
    });
    const [loading, setLoading] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert("Le fichier ne doit pas dépasser 5 Mo");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setDocument(prev => ({ ...prev, fichier: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        if (!document.nom_document || !document.fichier) {
            alert("Veuillez remplir tous les champs");
            return;
        }
        
        setLoading(true);
        await onAdd({ ...document, employe_id: employeId });
        setLoading(false);
        setDocument({ type_document: 'CNI', nom_document: '', date_expiration: '', fichier: null });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                <Modal.Title><i className="ri-file-add-line me-2"></i>Ajouter un document</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Type de document</Form.Label>
                        <Form.Select
                            value={document.type_document}
                            onChange={(e) => setDocument(prev => ({ ...prev, type_document: e.target.value }))}
                        >
                            <option value="CNI">CNI</option>
                            <option value="Passeport">Passeport</option>
                            <option value="Permis">Permis de conduire</option>
                            <option value="Diplome">Diplôme</option>
                            <option value="Attestation">Attestation</option>
                            <option value="Autre">Autre</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Nom du document</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ex: CNI 2024"
                            value={document.nom_document}
                            onChange={(e) => setDocument(prev => ({ ...prev, nom_document: e.target.value }))}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Date d'expiration</Form.Label>
                        <Form.Control
                            type="date"
                            value={document.date_expiration}
                            onChange={(e) => setDocument(prev => ({ ...prev, date_expiration: e.target.value }))}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Fichier</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.png,.doc,.docx"
                            onChange={handleFileChange}
                        />
                        <Form.Text className="text-muted">PDF, JPG, PNG, DOC, DOCX (max 5 Mo)</Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>Annuler</Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading} style={{ background: '#21ACCD', border: 'none' }}>
                    {loading ? <Spinner size="sm" /> : 'Ajouter'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};





// Modal de visualisation de document
const DocumentViewerModal = ({ show, onHide, document }) => {
    const [isImage, setIsImage] = useState(false);
    const [isPdf, setIsPdf] = useState(false);
    
    useEffect(() => {
        if (document && document.fichier) {
            const fileType = document.fichier.split(';')[0];
            setIsImage(fileType.includes('image'));
            setIsPdf(fileType.includes('pdf'));
        }
    }, [document]);
    
    if (!document) return null;
    
    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="document-viewer-modal">
            <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                <Modal.Title>
                    <i className="ri-file-view-line me-2"></i>
                    {document.nom_document}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="document-viewer-container">
                    <div className="document-info-bar p-3 bg-light border-bottom">
                        <div className="row">
                            <div className="col-md-6">
                                <small className="text-muted">Type de document</small>
                                <div><Badge bg="info">{document.type_document}</Badge></div>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted">Date d'ajout</small>
                                <div>{formatDate(document.created_at)}</div>
                            </div>
                            {document.date_expiration && document.date_expiration !== "0000-00-00" && (
                                <div className="col-md-12 mt-2">
                                    <small className="text-muted">Date d'expiration</small>
                                    <div className={new Date(document.date_expiration) < new Date() ? 'text-danger fw-bold' : ''}>
                                        {formatDate(document.date_expiration)}
                                        {new Date(document.date_expiration) < new Date() && (
                                            <i className="ri-error-warning-line ms-1" title="Document expiré"></i>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="document-content p-3">
                        {isImage ? (
                            <div className="text-center">
                                <img 
                                    src={document.fichier} 
                                    alt={document.nom_document}
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '70vh', objectFit: 'contain' }}
                                />
                            </div>
                        ) : isPdf ? (
                            <iframe
                                src={document.fichier}
                                title={document.nom_document}
                                style={{ width: '100%', height: '70vh', border: 'none', borderRadius: '8px' }}
                            />
                        ) : (
                            <div className="text-center py-5">
                                <i className="ri-file-line fs-48 text-muted mb-3 d-block"></i>
                                <p>Aperçu non disponible pour ce type de fichier</p>
                                <Button 
                                    variant="primary" 
                                    onClick={() => window.open(document.fichier, '_blank')}
                                    style={{ background: '#21ACCD', border: 'none' }}
                                >
                                    <i className="ri-download-line me-2"></i>
                                    Télécharger le fichier
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} className="rounded-pill">
                    Fermer
                </Button>
                <Button 
                    variant="primary" 
                    onClick={() => window.open(document.fichier, '_blank')}
                    style={{ background: '#21ACCD', border: 'none' }}
                    className="rounded-pill"
                >
                    <i className="ri-download-line me-2"></i>
                    Télécharger
                </Button>
            </Modal.Footer>
        </Modal>
    );
};



// Composant de modal pour ajouter un congé
// Composant de modal pour ajouter un congé - Version corrigée
const AddCongeModal = ({ show, onHide, onAdd, employeId }) => {
    const [conge, setConge] = useState({
        type_conge: 'annuel',
        date_debut: '',
        date_fin: '',
        motif: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!conge.date_debut) newErrors.date_debut = "La date de début est requise";
        if (!conge.date_fin) newErrors.date_fin = "La date de fin est requise";
        if (conge.date_debut && conge.date_fin && new Date(conge.date_fin) < new Date(conge.date_debut)) {
            newErrors.date_fin = "La date de fin doit être postérieure à la date de début";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        
        setLoading(true);
        try {
            await onAdd(conge);
            // Réinitialiser le formulaire après succès
            setConge({ type_conge: 'annuel', date_debut: '', date_fin: '', motif: '' });
            setErrors({});
        } catch (err) {
            console.error("Erreur lors de l'ajout:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="md">
            <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                <Modal.Title>
                    <i className="ri-calendar-add-line me-2"></i>
                    Ajouter un congé
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Type de congé <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                            value={conge.type_conge}
                            onChange={(e) => setConge(prev => ({ ...prev, type_conge: e.target.value }))}
                        >
                            <option value="annuel">Annuel</option>
                            <option value="maladie">Maladie</option>
                            <option value="sans_solde">Sans solde</option>
                            <option value="maternite">Maternité</option>
                            <option value="formation">Formation</option>
                        </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Date de début <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="date"
                            value={conge.date_debut}
                            onChange={(e) => setConge(prev => ({ ...prev, date_debut: e.target.value }))}
                            isInvalid={!!errors.date_debut}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.date_debut}
                        </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Date de fin <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="date"
                            value={conge.date_fin}
                            onChange={(e) => setConge(prev => ({ ...prev, date_fin: e.target.value }))}
                            isInvalid={!!errors.date_fin}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.date_fin}
                        </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Motif (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={conge.motif}
                            onChange={(e) => setConge(prev => ({ ...prev, motif: e.target.value }))}
                            placeholder="Raison du congé..."
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
                    Annuler
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit} 
                    disabled={loading}
                    style={{ background: '#21ACCD', border: 'none' }}
                >
                    {loading ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : 'Ajouter le congé'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Composant Modal de suppression de document
const DeleteDocumentModal = ({ show, onHide, onConfirm, document }) => {
    const [deleting, setDeleting] = useState(false);

    const handleConfirm = async () => {
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };

    if (!document) return null;

    return (
        <Modal show={show} onHide={onHide} centered className="delete-doc-modal" backdrop="static">
            <div className="delete-doc-container">
                <div className="delete-doc-icon-animation">
                    <div className="delete-doc-icon-wrapper">
                        <i className="ri-trash text-white"></i>
                    </div>
                    <div className="delete-doc-icon-bg"></div>
                </div>
                <Modal.Body className="delete-doc-body">
                    <h3 className="delete-doc-title">Supprimer le document ?</h3>
                    <p className="delete-doc-description">
                        Cette action est irréversible. Le document sera définitivement supprimé.
                    </p>
                    <div className="delete-doc-info">
                        <div className="doc-type-badge">
                            <i className="ri-file-text-line"></i>
                            <span>{document.type_document}</span>
                        </div>
                        <div className="doc-name">
                            <i className="ri-file-copy-line"></i>
                            <strong>{document.nom_document}</strong>
                        </div>
                        {document.date_expiration && document.date_expiration !== "0000-00-00" && (
                            <div className="doc-expiration">
                                <i className="ri-calendar-close-line"></i>
                                <span>Expire le {formatDate(document.date_expiration)}</span>
                                {new Date(document.date_expiration) < new Date() && (
                                    <span className="expired-badge">(Expiré)</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="delete-doc-warning">
                        <i className="ri-alert-line"></i>
                        <div className="warning-text">
                            <strong>Attention !</strong>
                            <span>Cette suppression est définitive et ne peut pas être annulée.</span>
                        </div>
                    </div>
                    <div className="delete-doc-actions">
                        <Button variant="outline-secondary" onClick={onHide} className="delete-doc-cancel-btn" disabled={deleting}>
                            <i className="ri-close-line"></i> Annuler
                        </Button>
                        <Button variant="danger" onClick={handleConfirm} disabled={deleting} className="delete-doc-confirm-btn">
                            {deleting ? (
                                <><Spinner size="sm" className="me-2" />Suppression...</>
                            ) : (
                                <><i className="ri-delete-bin-line"></i> Supprimer le document</>
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </div>
        </Modal>
    );
};

// Composant Modal de suppression de congé
const DeleteCongeModal = ({ show, onHide, onConfirm, conge }) => {
    const [deleting, setDeleting] = useState(false);

    const handleConfirm = async () => {
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };

    if (!conge) return null;

    return (
        <Modal show={show} onHide={onHide} centered className="delete-conge-modal" backdrop="static">
            <div className="delete-conge-container">
                <div className="delete-conge-icon-animation">
                    <div className="delete-conge-icon-wrapper">
                        <i className="ri-calendar-delete-line"></i>
                    </div>
                    <div className="delete-conge-icon-bg"></div>
                </div>
                <Modal.Body className="delete-conge-body">
                    <h3 className="delete-conge-title">Supprimer ce congé ?</h3>
                    <p className="delete-conge-description">
                        Cette action est irréversible. La demande de congé sera définitivement supprimée.
                    </p>
                    <div className="delete-conge-info">
                        <div className="conge-type-badge">
                            <i className="ri-calendar-line"></i>
                            <span>{conge.type_conge}</span>
                        </div>
                        <div className="conge-period">
                            <i className="ri-calendar-event-line"></i>
                            <span>Du {formatDate(conge.date_debut)} au {formatDate(conge.date_fin)}</span>
                        </div>
                        {conge.motif && (
                            <div className="conge-motif">
                                <i className="ri-chat-quote-line"></i>
                                <span>{conge.motif}</span>
                            </div>
                        )}
                    </div>
                    <div className="delete-conge-warning">
                        <i className="ri-alert-line"></i>
                        <div className="warning-text">
                            <strong>Attention !</strong>
                            <span>Cette suppression est définitive et ne peut pas être annulée.</span>
                        </div>
                    </div>
                    <div className="delete-conge-actions">
                        <Button variant="outline-secondary" onClick={onHide} className="delete-conge-cancel-btn" disabled={deleting}>
                            <i className="ri-close-line"></i> Annuler
                        </Button>
                        <Button variant="danger" onClick={handleConfirm} disabled={deleting} className="delete-conge-confirm-btn">
                            {deleting ? (
                                <><Spinner size="sm" className="me-2" />Suppression...</>
                            ) : (
                                <><i className="ri-delete-bin-line"></i> Supprimer le congé</>
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </div>
        </Modal>
    );
};

export default function DetailEmploye() {
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [employe, setEmploye] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showCongeModal, setShowCongeModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    
    // États pour les modals de suppression
    const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [showDeleteCongeModal, setShowDeleteCongeModal] = useState(false);
    const [congeToDelete, setCongeToDelete] = useState(null);


    const [showDocumentViewer, setShowDocumentViewer] = useState(false);
const [selectedDocument, setSelectedDocument] = useState(null);

const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowDocumentViewer(true);
};
    useEffect(() => {
        if (id) {
            fetchEmploye();
        } else {
            setError("ID de l'employé non spécifié");
            setLoading(false);
        }
    }, [id]);

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };

    const hideToast = () => {
        setToast({ show: false, type: '', title: '', message: '' });
    };

    const fetchEmploye = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/pages/signin2");
                return;
            }
            
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error("Employé non trouvé");
                } else if (response.status === 401) {
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.success && result.data) {
                setEmploye(result.data);
                setEditForm(result.data);
            } else {
                throw new Error(result.message || "Employé non trouvé");
            }
        } catch (err) {
            console.error("Erreur fetchEmploye:", err);
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                showToast('success', 'Succès', "Employé supprimé avec succès !");
                setTimeout(() => {
                    navigate('/rh/employes');
                }, 2000);
            } else {
                throw new Error(result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = async () => {
        setEditing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editForm)
            });

            const result = await response.json();

            if (result.success) {
                setShowEditModal(false);
                await fetchEmploye();
                showToast('success', 'Succès', "Employé modifié avec succès !");
            } else {
                throw new Error(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setEditing(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddDocument = async (document) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'document',
                    ...document
                })
            });

            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Document ajouté avec succès");
                fetchEmploye();
                setShowDocumentModal(false);
            } else {
                throw new Error(result.message || "Erreur lors de l'ajout");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    const handleDeleteDocumentClick = (doc) => {
        setDocumentToDelete(doc);
        setShowDeleteDocumentModal(true);
    };

    const handleConfirmDeleteDocument = async () => {
        if (!documentToDelete) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?document_id=${documentToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Document supprimé avec succès");
                fetchEmploye();
                setShowDeleteDocumentModal(false);
                setDocumentToDelete(null);
            } else {
                throw new Error(result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

   const handleAddConge = async (conge) => {
    try {
        const token = localStorage.getItem("token");
        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
        
        // AFFICHER LES DONNÉES DANS LA CONSOLE POUR DÉBOGUER
        console.log("=== DÉBOGAGE AJOUT CONGÉ ===");
        console.log("1. Données reçues du formulaire:", conge);
        console.log("2. Employe ID:", employe.id);
        console.log("3. Utilisateur courant:", currentUser);
        
        // Construction de l'objet à envoyer
        const congeData = {
            action: 'conge',
            employe_id: employe.id,
            type_conge: conge.type_conge,
            date_debut: conge.date_debut,
            date_fin: conge.date_fin,
            motif: conge.motif || '',
            created_by: currentUser.id_utilisateur || currentUser.id || 1
        };
        
        console.log("4. Données à envoyer:", JSON.stringify(congeData, null, 2));
        
        // Vérification des données avant envoi
        if (!congeData.employe_id) {
            throw new Error("L'ID de l'employé est manquant");
        }
        if (!congeData.date_debut) {
            throw new Error("La date de début est manquante");
        }
        if (!congeData.date_fin) {
            throw new Error("La date de fin est manquante");
        }
        if (new Date(congeData.date_fin) < new Date(congeData.date_debut)) {
            throw new Error("La date de fin doit être postérieure à la date de début");
        }
        
        console.log("5. Envoi de la requête...");
        
        const response = await fetch(`${API_BASE_URL}/employes.php`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(congeData)
        });
        
        console.log("6. Status de la réponse:", response.status);
        
        // Lire la réponse brute pour déboguer
        const responseText = await response.text();
        console.log("7. Réponse brute:", responseText);
        
        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            console.error("8. Erreur de parsing JSON:", e);
            throw new Error("La réponse du serveur n'est pas du JSON valide: " + responseText.substring(0, 200));
        }
        
        console.log("9. Résultat parsé:", result);
        
        if (result.success) {
            showToast('success', 'Succès', "Congé ajouté avec succès");
            fetchEmploye();
            setShowCongeModal(false);
        } else {
            throw new Error(result.message || "Erreur lors de l'ajout");
        }
    } catch (err) {
        console.error("=== ERREUR COMPLÈTE ===");
        console.error("Message:", err.message);
        console.error("Stack:", err.stack);
        showToast('error', 'Erreur', err.message);
    }
};
    const handleDeleteCongeClick = (conge) => {
    console.log("=== SUPPRESSION CONGÉ ===");
    console.log("Congé à supprimer:", conge);
    console.log("ID du congé:", conge.id);
    
    if (!conge || !conge.id) {
        showToast('error', 'Erreur', "ID du congé manquant");
        return;
    }
    
    setCongeToDelete(conge);
    setShowDeleteCongeModal(true);
};

   const handleConfirmDeleteConge = async () => {
    if (!congeToDelete || !congeToDelete.id) {
        showToast('error', 'Erreur', "Aucun congé sélectionné pour la suppression");
        setShowDeleteCongeModal(false);
        return;
    }
    
    console.log("Suppression du congé ID:", congeToDelete.id);
    
    try {
        const token = localStorage.getItem("token");
        
        // IMPORTANT: L'URL doit être correcte
        const url = `${API_BASE_URL}/employes.php?conge_id=${congeToDelete.id}`;
        console.log("URL d'appel:", url);
        
        const response = await fetch(url, {
            method: 'DELETE',
            headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        console.log("Status réponse:", response.status);
        
        const result = await response.json();
        console.log("Résultat:", result);
        
        if (result.success) {
            showToast('success', 'Succès', "Congé supprimé avec succès");
            fetchEmploye(); // Recharger la liste
            setShowDeleteCongeModal(false);
            setCongeToDelete(null);
        } else {
            throw new Error(result.message || "Erreur lors de la suppression");
        }
    } catch (err) {
        console.error("Erreur suppression congé:", err);
        showToast('error', 'Erreur', err.message);
        setShowDeleteCongeModal(false);
    }
};

    const handleUpdateCongeStatus = async (congeId, statut) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ conge_id: congeId, statut })
            });

            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', `Congé ${statut === 'approuve' ? 'approuvé' : 'refusé'}`);
                fetchEmploye();
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    const getStatutBadge = (statut) => {
        switch (statut) {
            case 'actif': return { variant: 'success', text: 'Actif', icon: 'ri-checkbox-circle-line' };
            case 'inactif': return { variant: 'secondary', text: 'Inactif', icon: 'ri-user-settings-line' };
            case 'suspendu': return { variant: 'danger', text: 'Suspendu', icon: 'ri-pause-circle-line' };
            default: return { variant: 'secondary', text: 'Inconnu', icon: 'ri-question-line' };
        }
    };

    const getCongeStatusBadge = (statut) => {
        switch (statut) {
            case 'approuve': return { variant: 'success', text: 'Approuvé' };
            case 'refuse': return { variant: 'danger', text: 'Refusé' };
            default: return { variant: 'warning', text: 'En attente' };
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des informations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !employe) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Employé non trouvé"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/rh/employes')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour à la liste
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const statut = getStatutBadge(employe.statut);
    const age = employe.date_naissance ? new Date().getFullYear() - new Date(employe.date_naissance).getFullYear() : null;

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    
                    <ToastNotification 
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-user-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Fiche employé</h4>
                                    <p className="text-muted small mb-0">
                                        {employe.prenom} {employe.nom} - {employe.matricule}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => navigate('/rh/employes')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => setShowEditModal(true)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                style={{ color: '#21ACCD', borderColor: '#21ACCD' }}
                            >
                                <i className="ri-edit-line"></i>
                                Modifier
                            </Button>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-delete-bin-line"></i>
                                Supprimer
                            </Button>
                        </div>
                    </div>

                    {/* En-tête du profil */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={2} className="text-center">
                                    <div className="profile-avatar mb-3 mb-md-0">
                                        {employe.photo ? (
                                            <Image src={employe.photo} roundedCircle className="profile-img" />
                                        ) : (
                                            <div className="avatar-placeholder">
                                                <i className="ri-user-line fs-1"></i>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                                <Col md={7}>
                                    <h3 className="mb-1">{employe.prenom} {employe.nom}</h3>
                                    <div className="d-flex flex-wrap gap-3 mb-2">
                                        <Badge bg={statut.variant} className="rounded-pill">
                                            <i className={`${statut.icon} me-1`}></i>
                                            {statut.text}
                                        </Badge>
                                        <Badge bg="info" className="rounded-pill">
                                            <i className="ri-briefcase-line me-1"></i>
                                            {employe.poste || 'Poste non défini'}
                                        </Badge>
                                        <Badge bg="secondary" className="rounded-pill">
                                            <i className="ri-building-line me-1"></i>
                                            {employe.departement || 'Département non défini'}
                                        </Badge>
                                    </div>
                                    <div className="text-muted small">
                                        <i className="ri-mail-line me-2"></i>{employe.email || 'Email non renseigné'}
                                        {employe.telephone && <><i className="ri-phone-line ms-3 me-2"></i>{employe.telephone}</>}
                                    </div>
                                </Col>
                                <Col md={3} className="text-md-end">
                                    <div className="info-card">
                                        <div className="info-label">Matricule</div>
                                        <div className="info-value fw-bold">{employe.matricule}</div>
                                        <div className="info-label mt-2">Date d'embauche</div>
                                        <div className="info-value">{formatDate(employe.date_embauche)}</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tabs */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="bg-transparent border-0 pt-4">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                <Nav.Item>
                                    <Nav.Link eventKey="info">
                                        <i className="ri-information-line me-2"></i>Informations
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="documents">
                                        <i className="ri-file-list-line me-2"></i>Documents
                                        {employe.documents?.length > 0 && (
                                            <Badge bg="primary" className="ms-2 rounded-pill">{employe.documents.length}</Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="conges">
                                        <i className="ri-calendar-line me-2"></i>Congés
                                        {employe.conges?.length > 0 && (
                                            <Badge bg="warning" className="ms-2 rounded-pill">{employe.conges.length}</Badge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Tab.Content>
                                {/* Onglet Informations */}
                                <Tab.Pane eventKey="info" active={activeTab === 'info'}>
                                    <Row>
                                        <Col md={6}>
                                            <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                <i className="ri-user-line me-2"></i>Informations personnelles
                                            </h6>
                                            <Table borderless className="info-table">
                                                <tbody>
                                                    <tr><td className="label">Nom complet</td><td>{employe.prenom} {employe.nom}</td></tr>
                                                    <tr><td className="label">Date de naissance</td><td>{formatDate(employe.date_naissance)} {age && `(${age} ans)`}</td></tr>
                                                    <tr><td className="label">Lieu de naissance</td><td>{employe.lieu_naissance || '-'}</td></tr>
                                                    <tr><td className="label">Nationalité</td><td>{employe.nationalite || '-'}</td></tr>
                                                    <tr><td className="label">Sexe</td><td>{employe.sexe === 'M' ? 'Masculin' : 'Féminin'}</td></tr>
                                                    <tr><td className="label">Situation matrimoniale</td><td>{employe.situation_matrimoniale || '-'}</td></tr>
                                                    <tr><td className="label">Nombre d'enfants</td><td>{employe.nombre_enfants || 0}</td>
                                                    </tr>
                                                    <tr><td className="label">Adresse</td><td>{employe.adresse || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                        <Col md={6}>
                                            <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                                <i className="ri-briefcase-line me-2"></i>Informations professionnelles
                                            </h6>
                                            <Table borderless className="info-table">
                                                <tbody>
                                                    <tr><td className="label">Matricule</td><td>{employe.matricule}</td>
                                                    </tr>
                                                    <tr><td className="label">Poste</td><td>{employe.poste || '-'}</td>
                                                    </tr>
                                                    <tr><td className="label">Département</td><td>{employe.departement || '-'}</td>
                                                    </tr>
                                                    <tr><td className="label">Date d'embauche</td><td>{formatDate(employe.date_embauche)}</td>
                                                    </tr>
                                                    <tr><td className="label">Type de contrat</td><td>{employe.type_contrat || '-'}</td>
                                                    </tr>
                                                    <tr><td className="label">Salaire de base</td><td>{new Intl.NumberFormat('fr-FR').format(employe.salaire_base || 0)} FCFA</td>
                                                    </tr>
                                                    <tr><td className="label">Email professionnel</td><td>{employe.email || '-'}</td>
                                                    </tr>
                                                    <tr><td className="label">Téléphone</td><td>{employe.telephone || '-'}</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </Tab.Pane>

                                {/* Onglet Documents */}
                                <Tab.Pane eventKey="documents" active={activeTab === 'documents'}>
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button 
                                            size="sm" 
                                            variant="outline-primary" 
                                            onClick={() => setShowDocumentModal(true)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-file-add-line me-1"></i>Ajouter un document
                                        </Button>
                                    </div>
                                    {employe.documents && employe.documents.length > 0 ? (
                                        <Table responsive className="align-middle documents-table">
    <thead className="bg-light">
        <tr>
            <th className="col-type">Type</th>
            <th className="col-nom-document">Nom du document</th>
            <th className="col-expiration">Date d'expiration</th>
            <th className="col-date">Date d'ajout</th>
            <th className="col-actions text-center">Actions</th>
        </tr>
    </thead>
    <tbody>
        {employe.documents.map(doc => (
            <tr key={doc.id}>
                <td className="col-type"><Badge bg="info">{doc.type_document}</Badge></td>
                <td className="col-nom-document">
                    <strong>{doc.nom_document}</strong>
                    {doc.fichier && (
                        <div className="mt-1">
                            <Button 
                                size="sm" 
                                variant="link" 
                                className="p-0 text-decoration-none"
                                onClick={() => handleViewDocument(doc)}
                            >
                                <i className="ri-eye-line me-1"></i>
                                Voir le document
                            </Button>
                        </div>
                    )}
                </td>
                <td className="col-expiration">
                    {doc.date_expiration && doc.date_expiration !== "0000-00-00" ? (
                        <span className={new Date(doc.date_expiration) < new Date() ? 'text-danger fw-semibold' : ''}>
                            {formatDate(doc.date_expiration)}
                            {new Date(doc.date_expiration) < new Date() && (
                                <i className="ri-error-warning-line ms-1 text-danger" title="Document expiré"></i>
                            )}
                        </span>
                    ) : '-'}
                </td>
                <td className="col-date">{formatDate(doc.created_at)}</td>
                <td className="col-actions text-center">
               
                
                    <Button 
                        size="sm" 
                        variant="outline-danger" 
                        onClick={() => handleDeleteDocumentClick(doc)}
                        className="rounded-pill"
                        title="Supprimer le document"
                    >
                        <i className="ri-delete-bin-line"></i>
                    </Button>
                </td>
            </tr>
        ))}
    </tbody>
</Table>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="ri-file-list-line fs-48 text-muted mb-3 d-block"></i>
                                            <p className="text-muted">Aucun document</p>
                                            <Button variant="outline-primary" onClick={() => setShowDocumentModal(true)}>
                                                Ajouter un document
                                            </Button>
                                        </div>
                                    )}
                                </Tab.Pane>

                                {/* Onglet Congés */}
                                <Tab.Pane eventKey="conges" active={activeTab === 'conges'}>
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button 
                                            size="sm" 
                                            variant="outline-primary" 
                                            onClick={() => setShowCongeModal(true)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-calendar-add-line me-1"></i>Ajouter un congé
                                        </Button>
                                    </div>
                                    {employe.conges && employe.conges.length > 0 ? (
                                        <Table responsive className="align-middle congés-table">
    <thead className="bg-light">
        <tr>
            <th style={{ width: '100px' }}>Type</th>
            <th style={{ width: '220px' }}>Période</th>
            <th style={{ width: '30%' }}>Motif</th>
            <th style={{ width: '100px' }}>Statut</th>
            <th style={{ width: '120px' }}>Date demande</th>
            <th style={{ width: '120px' }} className="text-center">Actions</th>
        </tr>
    </thead>
    <tbody>
        {employe.conges.map(conge => {
            const congeStatut = getCongeStatusBadge(conge.statut);
            // Calculer la durée en jours
            const dateDebut = new Date(conge.date_debut);
            const dateFin = new Date(conge.date_fin);
            const duree = Math.ceil((dateFin - dateDebut) / (1000 * 60 * 60 * 24)) + 1;
            
            return (
                <tr key={conge.id}>
                    <td>
                        <Badge bg="secondary" className="rounded-pill">
                            {conge.type_conge === 'annuel' && <i className="ri-sun-line me-1"></i>}
                            {conge.type_conge === 'maladie' && <i className="ri-hospital-line me-1"></i>}
                            {conge.type_conge === 'sans_solde' && <i className="ri-money-euro-circle-line me-1"></i>}
                            {conge.type_conge === 'maternite' && <i className="ri-baby-line me-1"></i>}
                            {conge.type_conge === 'formation' && <i className="ri-book-open-line me-1"></i>}
                            {conge.type_conge}
                        </Badge>
                    </td>
                    <td style={{ minWidth: '220px' }}>
                        <div className="d-flex flex-column">
                            <div className="fw-semibold">
                                <i className="ri-calendar-line me-1 text-muted"></i>
                                {formatDate(conge.date_debut)} → {formatDate(conge.date_fin)}
                            </div>
                            <small className="text-muted">
                                <i className="ri-time-line me-1"></i>
                                Durée: {duree} jour(s)
                            </small>
                        </div>
                    </td>
                    <td style={{ minWidth: '200px' }}>
                        {conge.motif ? (
                            <div className="text-truncate" style={{ maxWidth: '250px' }} title={conge.motif}>
                                {conge.motif}
                            </div>
                        ) : '-'}
                    </td>
                    <td>
                        <Badge bg={congeStatut.variant} className="rounded-pill">
                            {congeStatut.text === 'Approuvé' && <i className="ri-check-line me-1"></i>}
                            {congeStatut.text === 'Refusé' && <i className="ri-close-line me-1"></i>}
                            {congeStatut.text === 'En attente' && <i className="ri-time-line me-1"></i>}
                            {congeStatut.text}
                        </Badge>
                    </td>
                    <td>
                        <div className="small">
                            <i className="ri-calendar-todo-line me-1 text-muted"></i>
                            {formatDate(conge.created_at)}
                        </div>
                    </td>
                    <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                            {conge.statut === 'en_attente' && (
                                <>
                                    <Button 
                                        size="sm" 
                                        variant="success" 
                                        onClick={() => handleUpdateCongeStatus(conge.id, 'approuve')}
                                        className="rounded-pill"
                                        title="Approuver"
                                    >
                                        <i className="ri-check-line"></i>
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="danger" 
                                        onClick={() => handleUpdateCongeStatus(conge.id, 'refuse')}
                                        className="rounded-pill"
                                        title="Refuser"
                                    >
                                        <i className="ri-close-line"></i>
                                    </Button>
                                </>
                            )}
                            <Button 
                                size="sm" 
                                variant="outline-danger" 
                                onClick={() => handleDeleteCongeClick(conge)}
                                className="rounded-pill"
                                title="Supprimer le congé"
                            >
                                <i className="ri-delete-bin-line"></i>
                            </Button>
                        </div>
                    </td>
                </tr>
            );
        })}
    </tbody>
</Table>
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="ri-calendar-line fs-48 text-muted mb-3 d-block"></i>
                                            <p className="text-muted">Aucun congé enregistré</p>
                                            <Button variant="outline-primary" onClick={() => setShowCongeModal(true)}>
                                                Ajouter un congé
                                            </Button>
                                        </div>
                                    )}
                                </Tab.Pane>
                            </Tab.Content>
                        </Card.Body>
                    </Card>

                    <div className="metadata-section mt-4 text-center text-muted small">
                        <i className="ri-information-line me-1"></i>
                        Créé le {formatDate(employe.created_at)}
                        {employe.updated_at && employe.updated_at !== employe.created_at && ` • Modifié le ${formatDate(employe.updated_at)}`}
                    </div>
                </div>
                <Footer />
            </div>

            {/* Modal de suppression de l'employé */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer cet employé ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary d-block">{employe.prenom} {employe.nom}</strong>
                        <small className="text-muted">Matricule: {employe.matricule}</small>
                        <div className="mt-1">{employe.poste} - {employe.departement}</div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">Annuler</Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting} className="rounded-pill px-5">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de modification */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                    <Modal.Title><i className="ri-edit-line me-2"></i>Modifier l'employé</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="g-3">
                            <Col md={6}><Form.Group><Form.Label>Nom</Form.Label><Form.Control type="text" name="nom" value={editForm.nom || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Prénom</Form.Label><Form.Control type="text" name="prenom" value={editForm.prenom || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Email</Form.Label><Form.Control type="email" name="email" value={editForm.email || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Téléphone</Form.Label><Form.Control type="tel" name="telephone" value={editForm.telephone || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={12}><Form.Group><Form.Label>Adresse</Form.Label><Form.Control as="textarea" rows={2} name="adresse" value={editForm.adresse || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Poste</Form.Label><Form.Control type="text" name="poste" value={editForm.poste || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Département</Form.Label><Form.Control type="text" name="departement" value={editForm.departement || ''} onChange={handleEditChange} /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Type de contrat</Form.Label><Form.Select name="type_contrat" value={editForm.type_contrat || 'CDI'} onChange={handleEditChange}><option value="CDI">CDI</option><option value="CDD">CDD</option><option value="Stage">Stage</option><option value="Alternance">Alternance</option></Form.Select></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label>Salaire de base (FCFA)</Form.Label><Form.Control type="number" name="salaire_base" value={editForm.salaire_base || ''} onChange={handleEditChange} min="0" step="1000" /></Form.Group></Col>
                            <Col md={12}><Form.Group><Form.Label>Statut</Form.Label><Form.Select name="statut" value={editForm.statut || 'actif'} onChange={handleEditChange}><option value="actif">Actif</option><option value="inactif">Inactif</option><option value="suspendu">Suspendu</option></Form.Select></Form.Group></Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} className="rounded-pill">Annuler</Button>
                    <Button variant="primary" onClick={handleEdit} disabled={editing} className="rounded-pill" style={{ background: '#21ACCD', border: 'none' }}>{editing ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : 'Enregistrer'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Modals d'ajout */}
            <AddDocumentModal show={showDocumentModal} onHide={() => setShowDocumentModal(false)} onAdd={handleAddDocument} employeId={employe.id} />
            <AddCongeModal show={showCongeModal} onHide={() => setShowCongeModal(false)} onAdd={handleAddConge} employeId={employe.id} />

            {/* Modals de suppression de document et congé */}
            <DeleteDocumentModal show={showDeleteDocumentModal} onHide={() => { setShowDeleteDocumentModal(false); setDocumentToDelete(null); }} onConfirm={handleConfirmDeleteDocument} document={documentToDelete} />
            <DeleteCongeModal show={showDeleteCongeModal} onHide={() => { setShowDeleteCongeModal(false); setCongeToDelete(null); }} onConfirm={handleConfirmDeleteConge} conge={congeToDelete} />


{/* Modal de visualisation de document */}
<DocumentViewerModal
    show={showDocumentViewer}
    onHide={() => {
        setShowDocumentViewer(false);
        setSelectedDocument(null);
    }}
    document={selectedDocument}
/>
            <style>{`

            /* Modal de visualisation de document */
.document-viewer-modal .modal-content {
    border-radius: 20px;
    overflow: hidden;
    border: none;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.document-viewer-modal .modal-header {
    border-bottom: none;
    padding: 1rem 1.5rem;
}

.document-viewer-modal .modal-footer {
    border-top: 1px solid #e2e8f0;
    padding: 1rem 1.5rem;
}

.document-info-bar {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.document-content {
    max-height: 70vh;
    overflow: auto;
    background: #ffffff;
}

/* Animation d'entrée */
.document-viewer-modal .modal-content {
    animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(-20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

/* Responsive pour mobile */
@media (max-width: 576px) {
    .document-viewer-modal .modal-dialog {
        margin: 0.5rem;
    }
    
    .document-content {
        max-height: 60vh;
    }
}



                .profile-avatar { width: 100px; height: 100px; margin: 0 auto; }
                .profile-img { width: 100px; height: 100px; object-fit: cover; border: 3px solid #21ACCD; }
                .avatar-placeholder { width: 100px; height: 100px; background: #e2f3f5; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #21ACCD; border: 3px solid #21ACCD; }
                .info-table td { padding: 8px 0; border: none; }
                .info-table .label { width: 40%; color: #6c757d; font-size: 0.85rem; }
                .info-table .value { font-weight: 500; }
                .info-card { background: #f8fafc; padding: 10px 15px; border-radius: 12px; }
                .info-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; }
                .info-value { font-size: 0.9rem; }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .fs-48 { font-size: 3rem; }
                .modern-modal .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.2); overflow: hidden; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
                .toast-notification-fixed { animation: slideInRight 0.3s ease-out; }
                .nav-tabs { border-bottom: 1px solid #e2e8f0; }
                .nav-tabs .nav-link { color: #64748b; border: none; padding: 10px 20px; }
                .nav-tabs .nav-link:hover { color: #21ACCD; }
                .nav-tabs .nav-link.active { color: #21ACCD; border-bottom: 2px solid #21ACCD; background: transparent; }

                /* Styles pour le tableau des documents */
                .documents-table .col-type { width: 100px; }
                .documents-table .col-nom-document { width: 35%; min-width: 250px; }
                .documents-table .col-expiration { width: 130px; }
                .documents-table .col-date { width: 120px; }
                .documents-table .col-actions { width: 80px; }

                /* Styles pour les modals de suppression */
                .delete-doc-modal .modal-content, .delete-conge-modal .modal-content { background: transparent; border: none; box-shadow: none; }
                .delete-doc-container, .delete-conge-container { background: white; border-radius: 32px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); animation: modalSlideIn 0.4s cubic-bezier(0.16,1,0.3,1); }
                @keyframes modalSlideIn { from { opacity: 0; transform: scale(0.95) translateY(-20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                .delete-doc-icon-animation, .delete-conge-icon-animation { position: relative; height: 120px; background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); display: flex; justify-content: center; align-items: center; }
                .delete-doc-icon-wrapper, .delete-conge-icon-wrapper { width: 80px; height: 80px; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; z-index: 2; animation: iconPulse 0.5s ease-out; box-shadow: 0 10px 25px -5px rgba(239,68,68,0.4); }
                .delete-doc-icon-wrapper i, .delete-conge-icon-wrapper i { font-size: 3rem; color: white; animation: iconShake 0.5s ease-out; }
                .delete-doc-icon-bg, .delete-conge-icon-bg { position: absolute; width: 140px; height: 140px; background: rgba(239,68,68,0.1); border-radius: 50%; animation: bgPulse 1.5s infinite; }
                @keyframes iconPulse { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
                @keyframes iconShake { 0%,100% { transform: rotate(0deg); } 25% { transform: rotate(-10deg); } 75% { transform: rotate(10deg); } }
                @keyframes bgPulse { 0% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.2); opacity: 0.1; } 100% { transform: scale(1); opacity: 0.3; } }
                .delete-doc-body, .delete-conge-body { padding: 32px; }
                .delete-doc-title, .delete-conge-title { font-size: 1.5rem; font-weight: 700; text-align: center; color: #1e293b; margin-bottom: 12px; }
                .delete-doc-description, .delete-conge-description { text-align: center; color: #64748b; font-size: 0.9rem; margin-bottom: 24px; line-height: 1.5; }
                .delete-doc-info, .delete-conge-info { background: #f8fafc; border-radius: 20px; padding: 20px; margin-bottom: 24px; border: 1px solid #e2e8f0; }
                .doc-type-badge, .conge-type-badge { display: inline-flex; align-items: center; gap: 8px; background: #e2f3f5; padding: 6px 12px; border-radius: 50px; font-size: 0.8rem; font-weight: 500; color: #21ACCD; margin-bottom: 12px; }
                .doc-name { display: flex; align-items: center; gap: 8px; font-size: 1rem; margin-bottom: 8px; color: #1e293b; }
                .doc-expiration, .conge-period { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #64748b; margin-top: 8px; }
                .expired-badge { color: #ef4444; font-weight: 500; margin-left: 8px; }
                .conge-motif { display: flex; align-items: flex-start; gap: 8px; font-size: 0.85rem; color: #64748b; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0; }
                .delete-doc-warning, .delete-conge-warning { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 14px 16px; border-radius: 12px; display: flex; align-items: flex-start; gap: 12px; margin-bottom: 28px; }
                .delete-doc-warning i, .delete-conge-warning i { font-size: 1.25rem; color: #f59e0b; flex-shrink: 0; margin-top: 2px; }
                .delete-doc-actions, .delete-conge-actions { display: flex; gap: 16px; justify-content: flex-end; }
                .delete-doc-cancel-btn, .delete-conge-cancel-btn { padding: 10px 24px; border-radius: 50px; font-weight: 500; border: 1px solid #e2e8f0; color: #64748b; }
                .delete-doc-cancel-btn:hover, .delete-conge-cancel-btn:hover { background: #f8fafc; border-color: #cbd5e1; color: #475569; transform: translateY(-2px); }
                .delete-doc-confirm-btn, .delete-conge-confirm-btn { padding: 10px 28px; border-radius: 50px; font-weight: 600; background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border: none; box-shadow: 0 4px 10px rgba(239,68,68,0.3); }
                .delete-doc-confirm-btn:hover:not(:disabled), .delete-conge-confirm-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(239,68,68,0.4); background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
                @media (max-width: 576px) { .delete-doc-body, .delete-conge-body { padding: 24px; } .delete-doc-actions, .delete-conge-actions { flex-direction: column; } .delete-doc-cancel-btn, .delete-doc-confirm-btn, .delete-conge-cancel-btn, .delete-conge-confirm-btn { width: 100%; justify-content: center; } }
            `}</style>
        </>
    );
}